import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  DollarSign, Fuel, Wrench, ShieldCheck, Receipt, TrendingUp, Save,
  Loader2, RotateCcw, ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NumberInput = ({ label, prefix, suffix, value, onChange, step = 0.01, min = 0, testid }) => (
  <label className="block">
    <span className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>
    <div className="flex items-stretch border border-slate-300 rounded-md overflow-hidden bg-white focus-within:ring-2 focus-within:ring-[#002FA7]/30">
      {prefix && <span className="px-2 flex items-center text-slate-400 text-sm bg-slate-50 border-r border-slate-200">{prefix}</span>}
      <input
        type="number"
        step={step}
        min={min}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
        data-testid={testid}
        className="flex-1 px-3 py-2 text-sm focus:outline-none"
      />
      {suffix && <span className="px-2 flex items-center text-slate-400 text-xs bg-slate-50 border-l border-slate-200">{suffix}</span>}
    </div>
  </label>
);

const Section = ({ title, icon: Icon, children }) => (
  <section className="bg-white border border-slate-200 rounded-md p-5 mb-5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
    <h2 className="text-sm font-bold uppercase tracking-widest text-slate-700 mb-4 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
      <Icon className="w-4 h-4 text-[#002FA7]" /> {title}
    </h2>
    {children}
  </section>
);

const CostSettingsPage = () => {
  const [settings, setSettings] = useState(null);
  const [loadTypes, setLoadTypes] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/profitability/settings`, { withCredentials: true });
      setSettings(data.settings);
      setDefaults(data.defaults);
      setLoadTypes(data.load_types || []);
    } catch (e) {
      toast.error('Failed to load cost settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSave = useCallback(async () => {
    const snapshot = settings;
    if (!snapshot || saving) return;
    setSaving(true);
    const payload = {};
    for (const [k, v] of Object.entries(snapshot)) {
      if (v !== '' && v !== null && v !== undefined) payload[k] = v;
    }
    try {
      const { data } = await axios.put(`${API}/profitability/settings`, payload, { withCredentials: true });
      setSettings(data.settings);
      toast.success('Cost settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [settings, saving]);

  const resetToDefaults = () => {
    if (!defaults) return;
    if (!window.confirm('Reset every cost setting to CivilX TMS defaults? Your saved values will be replaced.')) return;
    setSettings({ ...defaults });
  };

  const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const setRpm = (lt, v) => setSettings((s) => ({ ...s, rpm_targets: { ...s.rpm_targets, [lt]: v } }));
  const setMargin = (lt, v) => setSettings((s) => ({ ...s, margin_targets: { ...s.margin_targets, [lt]: v } }));

  if (loading || !settings) {
    return (
      <DashboardLayout>
        <div className="p-6 md:p-8 max-w-5xl mx-auto pb-24 lg:pb-8" data-testid="cost-settings-page">
          <header className="mb-6">
            <div className="h-9 w-72 bg-slate-200 rounded animate-pulse mb-2" />
            <div className="h-4 w-96 bg-slate-100 rounded animate-pulse" />
          </header>
          {['Fuel', 'Driver Pay', 'Operating Costs', 'Profitability Floors', 'Per-load-type Targets'].map((label) => (
            <div key={label} className="bg-white border border-slate-200 rounded-md p-5 mb-5">
              <div className="h-4 w-32 bg-slate-200 rounded animate-pulse mb-4" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="h-10 bg-slate-100 rounded animate-pulse" />
                <div className="h-10 bg-slate-100 rounded animate-pulse" />
              </div>
            </div>
          ))}
          <p className="text-xs text-slate-400 flex items-center gap-2 mt-2">
            <Loader2 className="w-3 h-3 animate-spin" /> Loading your cost engine…
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto pb-24 lg:pb-8" data-testid="cost-settings-page">
        <header className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Cost & Profitability Settings
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Tune the engine that powers every load&apos;s RPM / CPM / profit / margin and the 🟢🟡🔴 status pill. Changes apply instantly to every new and recomputed load — historic loads keep their stored numbers until edited.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={resetToDefaults}
              className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 hover:border-slate-400 text-slate-700 rounded-md text-xs font-semibold"
              data-testid="cost-reset-defaults"
            >
              <RotateCcw className="w-3 h-3" /> Reset to defaults
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              data-testid="cost-save"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] hover:bg-[#00247D] text-white rounded-md text-sm font-semibold disabled:opacity-60"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Changes
            </button>
          </div>
        </header>

        <Section title="Fuel" icon={Fuel}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberInput label="Fleet MPG" value={settings.fleet_mpg} onChange={(v) => set('fleet_mpg', v)} step={0.1} suffix="mi/gal" testid="cost-mpg" />
            <NumberInput label="Fuel price" prefix="$" suffix="/ gal" value={settings.fuel_price_per_gallon} onChange={(v) => set('fuel_price_per_gallon', v)} testid="cost-fuel-price" />
          </div>
        </Section>

        <Section title="Driver Pay" icon={DollarSign}>
          <div className="mb-3 flex items-center gap-2">
            <span className="text-xs text-slate-600">Default mode for new loads:</span>
            {[
              { v: 'percent', l: 'Percentage of revenue' },
              { v: 'per_mile', l: 'Per-mile flat rate' },
            ].map((m) => (
              <button
                key={m.v}
                onClick={() => set('driver_pay_mode', m.v)}
                data-testid={`cost-driver-mode-${m.v}`}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md border ${settings.driver_pay_mode === m.v ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-700 border-slate-300'}`}
              >
                {m.l}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberInput label="Pay as % of revenue" suffix="%" value={settings.driver_pay_percent} onChange={(v) => set('driver_pay_percent', v)} testid="cost-driver-percent" />
            <NumberInput label="Per-mile pay (used when load is per-mile)" prefix="$" suffix="/ mi" value={settings.driver_pay_per_mile} onChange={(v) => set('driver_pay_per_mile', v)} testid="cost-driver-permile" />
          </div>
        </Section>

        <Section title="Operating Costs (per mile)" icon={Wrench}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <NumberInput label="Maintenance" prefix="$" suffix="/ mi" value={settings.maintenance_per_mile} onChange={(v) => set('maintenance_per_mile', v)} testid="cost-maint" />
            <NumberInput label="Insurance" prefix="$" suffix="/ mi" value={settings.insurance_per_mile} onChange={(v) => set('insurance_per_mile', v)} testid="cost-insurance" />
            <NumberInput label="IFTA Tax" prefix="$" suffix="/ mi" value={settings.ifta_per_mile} onChange={(v) => set('ifta_per_mile', v)} testid="cost-ifta" />
            <NumberInput label="Trailer (depreciation + reg)" prefix="$" suffix="/ mi" value={settings.trailer_per_mile} onChange={(v) => set('trailer_per_mile', v)} testid="cost-trailer" />
            <NumberInput label="Other operating costs" prefix="$" suffix="/ mi" value={settings.other_per_mile} onChange={(v) => set('other_per_mile', v)} testid="cost-other" />
            <NumberInput label="Default tolls (if not on load)" prefix="$" suffix="total" value={settings.default_toll_total} onChange={(v) => set('default_toll_total', v)} testid="cost-default-tolls" />
          </div>
        </Section>

        <Section title="Profitability Floors" icon={ShieldCheck}>
          <NumberInput label="Minimum profit (load NEVER turns green below this)" prefix="$" value={settings.minimum_profit_dollars} onChange={(v) => set('minimum_profit_dollars', v)} step={10} testid="cost-min-profit" />
        </Section>

        <Section title="Per-load-type Targets" icon={TrendingUp}>
          <p className="text-xs text-slate-500 mb-3">Minimum RPM (revenue per mile) and margin % a load must meet to be marked 🟢 PROFITABLE for each type.</p>
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 text-[10px] font-bold uppercase tracking-widest text-slate-500 pb-1 border-b border-slate-200">
              <div className="col-span-5">Load Type</div>
              <div className="col-span-3">Min RPM</div>
              <div className="col-span-3">Min Margin</div>
              <div className="col-span-1"></div>
            </div>
            {loadTypes.map(({ value, label }) => (
              <div key={value} className="grid grid-cols-12 gap-2 items-center" data-testid={`cost-loadtype-row-${value}`}>
                <div className="col-span-5 text-sm text-slate-800">{label}</div>
                <div className="col-span-3">
                  <NumberInput label="" prefix="$" value={settings.rpm_targets?.[value] ?? ''} onChange={(v) => setRpm(value, v)} testid={`cost-rpm-${value}`} />
                </div>
                <div className="col-span-3">
                  <NumberInput label="" suffix="%" value={settings.margin_targets?.[value] ?? ''} onChange={(v) => setMargin(value, v)} step={0.5} testid={`cost-margin-${value}`} />
                </div>
                <div className="col-span-1 flex justify-end">
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              </div>
            ))}
          </div>
        </Section>

        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6 flex items-start gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <Receipt className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-blue-900">
            <p className="font-bold uppercase tracking-widest mb-1">How the engine uses these</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li><strong>Power Only</strong> and <strong>Tow Away</strong> exclude trailer ownership cost automatically.</li>
              <li><strong>Drop &amp; Hook</strong> applies a 0.95 labor multiplier (reduced idle).</li>
              <li>A load is only marked 🟢 PROFITABLE if RPM ≥ target, margin &gt; target, AND profit ≥ the minimum-profit floor.</li>
              <li>Settings apply to every NEW load instantly; saved loads keep their stored numbers until you edit them.</li>
            </ul>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CostSettingsPage;
